import {
	Autocomplete,
	Grid,
	InputAdornment,
	TextField,
	Typography,
} from "@mui/material";
import { useContext, useEffect, useState, useRef, useMemo } from "react";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
	parkServePlaceLayerId,
	parkServeUrbanLayerId,
	placeIdField,
	placeObjectIdField,
	placeSearchField,
	placeServiceLayer,
	priorityAreaPlaceFLId,
	priorityAreaUrbanFLId,
	urbanIdField,
} from "../../constants";
import throttle from "lodash/throttle";
import CityContext from "../../contexts/CityContext";
import { CityProfileVisibleContext } from "../../contexts/CityProfileVisibleContext";
import { MapCityContext, MapViewContext } from "../../contexts/MapViewContext";
import FeatureFilter from "@arcgis/core/layers/support/FeatureFilter";
import { useSearchParams } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { trackEvent } from "../../hooks/useAnalytics";

interface Option {
	title: string;
	id: string;
	objectId: number;
	ParkServe_UrbanArea_ID: number;
}

// The search bar uses the Material UI autocomplete component along with the queryFeatures method on the place feature service to generate custom search results based on the user input

const TPLSearchBar = () => {
	const matches = useMediaQuery("(max-width:1000px)");
	const { city, setCity, urban, setUrban, isInitialLoad } =
		useContext(CityContext);
	const { mapCity, setMapCity } = useContext(MapCityContext);
	const [searchParams] = useSearchParams();
	const { visible, setVis } = useContext(CityProfileVisibleContext);
	const [value, setValue] = useState<Option | null>(null);
	const [options, setOptions] = useState<Option[]>([]);
	const [inputValue, setInputValue] = useState<string>("");
	const view = useContext(MapViewContext);
	let parkServeUrbanLayer: __esri.FeatureLayer;
	let parkServePlaceLayer: __esri.FeatureLayer;
	let priorityAreaUrbanFL: __esri.FeatureLayer;
	let priorityAreaPlaceFL: __esri.FeatureLayer;
	const graphicsLayer: any = view?.map.findLayerById("sketch-layer");
	const walkAreaGL: any = view?.map.findLayerById("walk-layer");

	const getOptions = useMemo(
		() =>
			throttle(
				async (
					input: string,
					callback: (results?: readonly { [key: string]: any }[]) => void
				) => {
					try {
						const { features } = await placeServiceLayer.queryFeatures({
							outFields: [
								placeObjectIdField,
								placeIdField,
								placeSearchField,
								urbanIdField,
							],
							returnGeometry: false,
							num: 20,
							orderByFields: ["TOTAL_POP DESC"],
							where: `LOWER(place_stabbrev) LIKE '${input.toLowerCase()}%'`,
						});
						callback(features.map((f: __esri.Graphic) => f.attributes));
					} catch (e) {
						console.log(e);
					}
				},
				500,
				{ leading: true, trailing: true }
			),
		[]
	);

	const onChange = (event: any, newValue: Option | null) => {
		//console.log(isInitialLoad);
		if (!isInitialLoad) {
			setValue(newValue);

			if (newValue) {
				console.log(newValue);
				trackEvent("city_search", newValue.title);
			}

			newValue && setMapCity(newValue.title);
			newValue && setCity?.(newValue.id);
			newValue && setUrban?.(newValue.ParkServe_UrbanArea_ID);

			if (matches) {
				setVis(false);
			} else {
				setVis(true);
			}
		}
	};

	useEffect(() => {
		if (searchParams && city) {
			//console.log("not the first time");
			//console.log(city.name);
			setMapCity(city.name);

			if (isInitialLoad) {
				setValue({
					title: city.attributes[placeSearchField],
					id: city.attributes[placeIdField],
					objectId: city.attributes[placeObjectIdField],
					ParkServe_UrbanArea_ID: city.attributes[urbanIdField],
				});
			}
		}
	}, [searchParams, city]);

	useEffect(() => {
		let active = true;

		if (inputValue === "") {
			setOptions(value ? [value] : []);
			return undefined;
		}

		getOptions(inputValue, (results) => {
			if (active) {
				let newOptions: Option[] = [];

				if (value && !results?.length) {
					newOptions = [value];
				}
				console.log(results)
				if (results) {
					newOptions = [
						...newOptions,
						...results.map(
							(r): Option => ({
								title: r[placeSearchField],
								id: r[placeIdField],
								objectId: r[placeObjectIdField],
								ParkServe_UrbanArea_ID: r[urbanIdField],
							})
						),
					];
				}

				if (newOptions.length == 0) {
					if (view) {
						if (graphicsLayer) {
							graphicsLayer.removeAll();
						}
						if (walkAreaGL) {
							walkAreaGL.removeAll();
						}
						if (view.map.findLayerById("prioritize")) {
							let customLayer = view.map.findLayerById("prioritize");
							customLayer.visible = false;
						}

						//@ts-ignore
						parkServePlaceLayer = view.map.findLayerById(parkServePlaceLayerId);
						//@ts-ignore
						parkServeUrbanLayer = view.map.findLayerById(parkServeUrbanLayerId);
						// @ts-ignore
						priorityAreaUrbanFL = view.map.findLayerById(priorityAreaUrbanFLId);
						// @ts-ignore
						priorityAreaPlaceFL = view.map.findLayerById(priorityAreaPlaceFLId);

						[
							parkServePlaceLayer,
							parkServeUrbanLayer,
							priorityAreaUrbanFL,
							priorityAreaPlaceFL,
						].forEach((layer) => {
							view.whenLayerView(layer).then((layerView) => {
								layerView.filter = new FeatureFilter({
									where: `1 = 1`,
								});
							});
						});
					}
				}
				console.log(newOptions)
				setOptions(newOptions);
			}
		});

		return () => {
			active = false;
		};
	}, [value, inputValue]);

	return (
		<Autocomplete
			disablePortal
			getOptionLabel={(option: { title: string }) => option.title}
			popupIcon={<KeyboardArrowDownIcon />}
			filterOptions={(x) => x}
			options={options}
			value={value}
			clearOnBlur={false}
			onChange={onChange}
			onInputChange={(e) => {
				if (e != null) setInputValue((e.target as HTMLInputElement).value);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					variant="outlined"
					color="darkGrey"
					id="search-box"
					InputProps={{
						...params.InputProps,
						sx: {
							root: { borderRadius: "0px" },
							p: "8px !important",
							maxWidth: "100%",
							minWidth: 200,
							width: matches ? "100%" : 400,
							"& .MuiAutocomplete-endAdornment": {
								position: "unset !important",
							},
							"& input": {
								color: (theme) => theme.palette.darkGrey.main,
								fontWeight: "bold",
								fontFamily: "PT Sans",
								fontSize: "16px",
								textAlign: "center",
							},
						},
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon />
							</InputAdornment>
						),
					}}
				/>
			)}
			renderOption={(props, option) => {
				return (
					<li {...props}>
						<Grid
							container
							alignItems="center"
						>
							<Typography
								variant="body2"
								color="text.secondary"
							>
								{option.title}
							</Typography>
						</Grid>
					</li>
				);
			}}
		/>
	);
};
export default TPLSearchBar;
